import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EmailDataModel } from 'src/app/shared-components/models/mailbox.model';
import { GetTiktokIntegration } from '@brand-lolly/app/_models/brand-tiktok-integration.model';
import { LoToastService } from 'lo-design-system';
import { BrandTiktokIntegrationService } from '@brand-lolly/app/services/brand-tiktok-integration.service';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import { SideUsageAlias } from '@brand-lolly/app/billing/types/billing.enums';
import { IntegrationsService } from './integrations.service';
import { EmailIntegrationCardComponent } from './components/email-integration-card.component';
import { TiktokIntegrationComponent } from './tiktok-integration/tiktok-integration.component';
import { GoogleDisclosureComponent } from './components/google-disclosure.component';
import { MetaIntegrationsBlockComponent } from './meta/meta-integrations-block.component';
import { ChatChannelsBlockComponent } from './chat-channels/chat-channels-block.component';
import { CHAT_RETURN_PARAMS } from './chat-channels/chat-channels.service';
import { AnaSettingAnchorDirective } from '@brand-lolly/components/ana-setting-anchor/ana-setting-anchor.directive';
import { AnaSettingsRevealService } from '@brand-lolly/app/_services/ana/ana-settings-reveal.service';

@Component({
	standalone: true,

	selector: 'app-integrations',
	templateUrl: './integrations.component.html',
	styleUrls: ['./integrations.component.scss'],
	imports: [
		CommonModule,
		RouterModule,
		EmailIntegrationCardComponent,
		MetaIntegrationsBlockComponent,
		TiktokIntegrationComponent,
		GoogleDisclosureComponent,
		ChatChannelsBlockComponent,
		AnaSettingAnchorDirective
	]
})
export class IntegrationsComponent implements OnInit, OnDestroy {
	public loading = true;
	public emailsLimit: number | null = null;
	public mailboxes: EmailDataModel[] = [];

	public tiktokData: GetTiktokIntegration[] = [];
	public tiktokLimit: number | null = null;
	public tiktokLoading = false;

	private openDialog = false;
	private unsubscriber$ = new Subject<void>();
	private settingsScope = `settings:integrations:${Math.random()
		.toString(36)
		.slice(2)}`;

	constructor(
		private integrationService: IntegrationsService,
		private toasts: LoToastService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private brandTiktokIntegrationService: BrandTiktokIntegrationService,
		private subscriptionsState: SubscriptionsStateService,
		private settingsReveal: AnaSettingsRevealService
	) {
		const showDialog =
			this.activatedRoute.snapshot.queryParamMap.get('openMailbox');
		if (showDialog === 'true') {
			this.openDialog = true;
			this.router.navigate([], {
				relativeTo: this.activatedRoute,
				queryParams: { openMailbox: null },
				queryParamsHandling: 'merge',
				replaceUrl: true
			});
		}
	}

	ngOnInit(): void {
		this.settingsReveal.registerPage(this.settingsScope, {
			keys: ['integrations.instagram', 'integrations.tiktok'],
			reload: () => this.getTiktokIntegrations()
		});
		this.activatedRoute.queryParams
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(x => {
				// `status` is a shared name on this route: the mailbox callback
				// spells its HTTP code there, and the chat install callback
				// spells `success` / `error` there too. Without this guard a
				// successful Slack connect renders "Failed to connect email!",
				// because `showEmailConnectStatus` reads anything it does not
				// recognise as a mailbox failure. `chat_platform` is the
				// discriminator the chat callback sends for exactly this reason;
				// `chat-channels-block` owns that return and clears its own
				// parameters.
				const status = x['status'];
				if (status && !x[CHAT_RETURN_PARAMS.platform]) {
					this.showEmailConnectStatus(status);
					this.clearQueryParams();
				}

				// TikTok connect is handled by <tiktok-integration> itself, so
				// skip it here to avoid a duplicate toast. Instagram/Meta has no
				// live sub-component owning this callback, so we handle it here.
				const integrationStatus = x['integrationStatus'];
				const integrationPlatform = x['integrationPlatform'];
				if (integrationStatus && integrationPlatform !== 'tiktok') {
					this.showIntegrationConnectStatus(
						integrationStatus,
						integrationPlatform,
						x['integrationAccountsCount'],
						x['integrationMessage']
					);
					this.clearIntegrationQueryParams();
				}
			});

		this.getConnectedEmails();
		this.getTiktokIntegrations();
	}

	getConnectedEmails(): void {
		this.loading = true;

		this.integrationService
			.getConnectedEmails()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: res => {
					this.mailboxes = res.data;
					this.emailsLimit =
						this.subscriptionsState.getSideEntitlementCap(
							SideUsageAlias.EMAIL_ACCOUNTS
						);
					this.loading = false;

					if (this.openDialog) {
						this.linkMailbox();
					}
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	linkMailbox(): void {
		this.integrationService.linkMailbox(
			this.mailboxes.length,
			this.emailsLimit,
			this.unsubscriber$
		);
	}

	onUnlink(item: EmailDataModel): void {
		this.loading = true;
		this.integrationService
			.uninstall(item.type, item.email)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: () => {
					this.toasts.success(
						`Disconnected ${item.type} Successfully`,
						{
							duration: 1200
						}
					);
					this.getConnectedEmails();
				},
				error: () => {
					this.loading = false;
					this.toasts.error(`Failed to disconnect ${item.type}`, {
						duration: 2000
					});
				}
			});
	}

	onUpgrade(): void {
		this.integrationService.navigateToUpgrade();
	}

	private clearQueryParams(): void {
		this.router.navigate([], {
			queryParams: { status: null },
			queryParamsHandling: 'merge'
		});
	}

	private clearIntegrationQueryParams(): void {
		this.router.navigate([], {
			queryParams: {
				integrationStatus: null,
				integrationPlatform: null,
				integrationAccountsCount: null,
				integrationMessage: null
			},
			queryParamsHandling: 'merge'
		});
	}

	private showIntegrationConnectStatus(
		status: string,
		platform?: string,
		count?: string,
		message?: string
	): void {
		const platformTitle = platform
			? platform.charAt(0).toUpperCase() + platform.slice(1)
			: 'account';

		if (status === 'success') {
			this.toasts.success(
				count
					? `Successfully connected ${count} ${platformTitle} accounts`
					: `Successfully connected ${platformTitle}`,
				{ duration: 10000 }
			);
		} else if (status === 'failed') {
			this.toasts.error(
				message ?? `Failed to connect with ${platformTitle}`,
				{
					duration: 10000
				}
			);
		}
	}

	private showEmailConnectStatus(status: string): void {
		switch (status) {
			case '201':
				this.toasts.success('Email connected successfully!', {
					duration: 10000
				});
				break;
			case '409':
				this.toasts.warning(
					'This email is already associated with another user account. Please try to disconnect it first before adding it to this account!',
					{ duration: 10000 }
				);
				break;
			case '400':
			default:
				this.toasts.error('Failed to connect email!', {
					duration: 10000
				});
				break;
		}
	}

	getTiktokIntegrations(): void {
		this.tiktokLoading = true;

		this.brandTiktokIntegrationService
			.getIntegrations()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: res => {
					this.tiktokData = res.data;
					this.tiktokLimit = res.limit;
					this.tiktokLoading = false;
				},
				error: () => {
					this.tiktokLoading = false;
				}
			});
	}

	ngOnDestroy(): void {
		this.settingsReveal.unregisterPage(this.settingsScope);
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}
}
